/**
 * utils/stampSvgGenerator.ts
 *
 * Source unique de vérité pour le rendu visuel du tampon circulaire d'avocat.
 * Génère le markup SVG complet sous forme de string, utilisé à la fois par :
 *   - StampPreview.tsx (aperçu en temps réel dans les paramètres)
 *   - pdfDossierBuilder.ts (rasterisation PNG → image embarquée dans le PDF)
 *
 * Garantit que l'aperçu et le tampon imprimé dans le dossier sont visuellement
 * identiques, car ils partagent exactement le même code de génération.
 *
 * Géométrie (viewBox 0 0 200 200) :
 *   - Cercle extérieur : r=90
 *   - Cercle intérieur : r=68
 *   - Arc supérieur (titre cabinet) : r=76, sens horaire 9h→12h→3h
 *   - Arc inférieur (toque) : r=80, sens anti-horaire 9h→6h→3h
 *   - Étoiles : x=21 et x=179, y=104
 */

const STAMP_COLOR = '#c53030';

export interface StampSvgParams {
  title: string;
  toqueNumber: string;
  pieceNumber?: number;
  uniqueId?: string;
  width?: number;
  height?: number;
}

function escapeXml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

export function generateStampSvg({
  title,
  toqueNumber,
  pieceNumber,
  uniqueId = 'stamp',
  width = 200,
  height = 200,
}: StampSvgParams): string {
  const rawId = uniqueId.replace(/[^a-zA-Z0-9_-]/g, '-');
  const safeId = /^\d/.test(rawId) ? `s-${rawId}` : rawId;
  const topArcId = `${safeId}-top`;
  const bottomArcId = `${safeId}-bot`;

  const displayTitle = escapeXml(title.trim() ? title.trim().toUpperCase() : 'VOTRE CABINET');
  const displayToque = escapeXml(`Toque ${toqueNumber.trim() || '000'}`);
  const hasRealTitle = title.trim().length > 0;
  const hasRealToque = toqueNumber.trim().length > 0;

  const starOpacity = hasRealTitle && hasRealToque ? '1' : '0.4';
  const titleOpacity = hasRealTitle ? '1' : '0.35';
  const toqueOpacity = hasRealToque ? '1' : '0.35';
  const nY = pieceNumber !== undefined ? '91' : '96';

  const numberText =
    pieceNumber !== undefined
      ? `<text x="100" y="120" text-anchor="middle" fill="${STAMP_COLOR}" font-size="30" font-family="Georgia, 'Times New Roman', serif" font-weight="bold">${pieceNumber}</text>`
      : '';

  return `<svg viewBox="0 0 200 200" width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Aperçu du tampon">
  <defs>
    <path id="${topArcId}" d="M 24 100 A 76 76 0 0 1 176 100"/>
    <path id="${bottomArcId}" d="M 20 100 A 80 80 0 0 0 180 100"/>
  </defs>
  <circle cx="100" cy="100" r="90" fill="none" stroke="${STAMP_COLOR}" stroke-width="2.5"/>
  <circle cx="100" cy="100" r="68" fill="none" stroke="${STAMP_COLOR}" stroke-width="1.5"/>
  <text x="21" y="104" text-anchor="middle" fill="${STAMP_COLOR}" font-size="9" font-family="serif" opacity="${starOpacity}">&#9733;</text>
  <text x="179" y="104" text-anchor="middle" fill="${STAMP_COLOR}" font-size="9" font-family="serif" opacity="${starOpacity}">&#9733;</text>
  <text fill="${STAMP_COLOR}" font-size="11.5" font-family="Georgia, 'Times New Roman', serif" font-weight="bold" letter-spacing="2" opacity="${titleOpacity}">
    <textPath href="#${topArcId}" startOffset="50%" text-anchor="middle">${displayTitle}</textPath>
  </text>
  <text fill="${STAMP_COLOR}" font-size="11" font-family="Georgia, 'Times New Roman', serif" letter-spacing="1.5" opacity="${toqueOpacity}">
    <textPath href="#${bottomArcId}" startOffset="50%" text-anchor="middle">${displayToque}</textPath>
  </text>
  <text x="100" y="76" text-anchor="middle" fill="${STAMP_COLOR}" font-size="15" font-family="Georgia, 'Times New Roman', serif" font-weight="bold" letter-spacing="4">PIECE</text>
  <text x="100" y="${nY}" text-anchor="middle" fill="${STAMP_COLOR}" font-size="13" font-family="Georgia, 'Times New Roman', serif" font-weight="bold" letter-spacing="2">N&#xB0;</text>
  ${numberText}
</svg>`;
}
